import { useState, useMemo } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useMarkAsOrdered } from '../hooks/useOrdering';
import { createMarkAsOrderedSchema } from '../schemas/orderingSchema';
import { getTodayDateString } from '@/shared/utils/dateUtils';
import { useBooleanSetting } from '@/shared/hooks/useSettings';
import type { Manufacturer, FrameReplenishmentItem } from '@/shared/types/domain.types';

interface OrderBatchFormProps {
  isOpen: boolean;
  onClose: () => void;
  manufacturer: Manufacturer;
  frames: FrameReplenishmentItem[];
  onSuccess: () => void;
}

export function OrderBatchForm({
  isOpen,
  onClose,
  manufacturer,
  frames,
  onSuccess,
}: OrderBatchFormProps) {
  const [poNumber, setPoNumber] = useState('');
  const [orderedDate, setOrderedDate] = useState(getTodayDateString());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { showToast } = useToast();
  const markAsOrdered = useMarkAsOrdered();

  // Fetch setting for PO number requirement (default: false/optional)
  const { data: requirePoNumber = false } = useBooleanSetting('require_po_number', false);

  // Extract frame IDs and count
  const frameIds = useMemo(() => frames.map((f) => f.id), [frames]);
  const frameCount = frames.length;

  // Check if frames come from multiple locations
  const multipleLocations = useMemo(() => {
    const locationIds = new Set(frames.map((f) => f.location_id));
    return locationIds.size > 1;
  }, [frames]);

  // Get unique location names for the warning
  const locationNames = useMemo(() => {
    const uniqueLocations = new Map<string, string>();
    frames.forEach((f) => {
      if (f.location) {
        uniqueLocations.set(f.location_id, f.location.name);
      }
    });
    return Array.from(uniqueLocations.values()).sort();
  }, [frames]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate with Zod schema (conditionally require PO number)
    const schema = createMarkAsOrderedSchema(requirePoNumber);
    const validation = schema.safeParse({
      frame_ids: frameIds,
      po_number: poNumber.trim(),
      ordered_date: orderedDate,
    });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    try {
      await markAsOrdered.mutateAsync({
        frame_ids: frameIds,
        po_number: poNumber.trim(),
        ordered_date: orderedDate,
      });

      const message = poNumber.trim()
        ? `${frameCount} frame${frameCount !== 1 ? 's' : ''} marked as ordered with PO #${poNumber.trim()}`
        : `${frameCount} frame${frameCount !== 1 ? 's' : ''} marked as ordered`;
      showToast(message, 'success');

      // Reset form
      setPoNumber('');
      setOrderedDate(getTodayDateString());
      setErrors({});

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error marking frames as ordered:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to mark frames as ordered',
        'error'
      );
    }
  };

  const handleClose = () => {
    if (!markAsOrdered.isPending) {
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Mark Frames as Ordered"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Order Summary</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-blue-700">Manufacturer:</span>
              <span className="font-medium text-blue-900">{manufacturer.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Number of Frames:</span>
              <span className="font-medium text-blue-900">
                {frameCount} frame{frameCount !== 1 ? 's' : ''}
              </span>
            </div>
            {manufacturer.min_order_qty && (
              <div className="flex justify-between">
                <span className="text-blue-700">Minimum Order Qty:</span>
                <span className="font-medium text-blue-900">
                  {manufacturer.min_order_qty} frames
                </span>
              </div>
            )}
            {manufacturer.rep_name && (
              <div className="flex justify-between">
                <span className="text-blue-700">Rep:</span>
                <span className="font-medium text-blue-900">{manufacturer.rep_name}</span>
              </div>
            )}
            {manufacturer.rep_phone && (
              <div className="flex justify-between">
                <span className="text-blue-700">Rep Phone:</span>
                <a
                  href={`tel:${manufacturer.rep_phone}`}
                  className="font-medium text-blue-600 hover:text-blue-800"
                >
                  {manufacturer.rep_phone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Below Minimum Warning */}
        {manufacturer.min_order_qty &&
          manufacturer.min_order_qty > 0 &&
          frameCount < manufacturer.min_order_qty && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-yellow-600 text-lg">⚠️</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Below Minimum Order Quantity
                  </p>
                  <p className="text-sm text-yellow-700">
                    You are ordering {frameCount} frame{frameCount !== 1 ? 's' : ''}, which
                    is below the minimum order quantity of {manufacturer.min_order_qty} for
                    this manufacturer. You can still proceed, but you may incur additional
                    fees.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Multiple Locations Warning */}
        {multipleLocations && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-orange-600 text-lg">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-800 mb-1">
                  Multiple Locations Detected
                </p>
                <p className="text-sm text-orange-700">
                  You are ordering frames from {locationNames.length} different locations in this batch
                  ({locationNames.join(', ')}). Please make sure your POs and shipments are
                  handled correctly for each location.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PO Number Input */}
        <Input
          label={requirePoNumber ? 'PO Number' : 'PO Number (Optional)'}
          type="text"
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          placeholder="e.g., PO-2024-001"
          required={requirePoNumber}
          error={errors.po_number}
          helperText="Letters, numbers, and dashes only"
          disabled={markAsOrdered.isPending}
          autoFocus
        />

        {/* Order Date Input */}
        <Input
          label="Order Date"
          type="date"
          value={orderedDate}
          onChange={(e) => setOrderedDate(e.target.value)}
          required
          error={errors.ordered_date}
          helperText="Date cannot be in the future"
          max={getTodayDateString()}
          disabled={markAsOrdered.isPending}
        />

        {/* Confirmation Message */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to mark these {frameCount} frame
            {frameCount !== 1 ? 's' : ''} as ordered? This will:
          </p>
          <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>Change their status from PENDING to ORDERED</li>
            <li>Set the PO number and order date</li>
            <li>Remove them from the pending frames list</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={markAsOrdered.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              markAsOrdered.isPending ||
              !orderedDate ||
              (requirePoNumber && !poNumber.trim())
            }
          >
            {markAsOrdered.isPending ? 'Marking as Ordered...' : 'Mark as Ordered'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
